/**
 * LeadFormAttach.tsx
 *
 * A lead that came in through a form carries the completed form
 * (`lead.eform`, a surveys_responses row). These pieces let staff put that form
 * on an existing patient's chart from LeadDetailView:
 *
 *  - `LeadFormNotice` — the one-line "Completed form: …" strip, or, once the
 *    form is on a chart, the line naming that patient with a link to them. It
 *    also hosts the patient search for a patient not among the suggestions.
 *  - `LeadFormAttachConfirm` — the confirm step, opened either from the search
 *    or from the "Add form to chart" button on a Patient Matches row.
 *
 * The attach endpoint (POST /patients/{id}/forms/attach-lead-form) also marks
 * the lead converted, which is why the confirm copy says it closes the lead and
 * why the lead and lead-list caches are refreshed afterwards. Refusals come back
 * as `{ error: { code, message } }` with a message written for staff, shown
 * as-is via getErrorMessage; on a refusal nothing is refreshed and the confirm
 * step stays open.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import { parseDateValue } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import type { LeadEform } from '@/services/leads.service';
import { attachLeadForm } from '@/services/patientForms.service';
import { toast } from '@/store/toastStore';

/** The patient a form is about to be added to. */
export interface FormAttachPatient {
  patientId: number;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
}

const ATTACH_FAILED_FALLBACK =
  "The form could not be added to the patient's chart. Please try again, and if it keeps happening, contact your administrator.";

/** Calendar date only: the stored value is a wall-clock time and must not be shifted. */
function formatCalendarDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = parseDateValue(value.trim().slice(0, 10));
  if (!date) return null;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function patientName(p: { firstName: string | null; lastName: string | null }): string {
  return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || 'this patient';
}

export function LeadFormNotice({
  eform,
  canAttach,
  onChoosePatient,
}: {
  eform: LeadEform;
  /** False once the lead is closed or the form is already on a chart. */
  canAttach: boolean;
  onChoosePatient: (patient: FormAttachPatient) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  if (eform.ownerPatientId !== null) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
        <ClipboardList className="h-4 w-4 text-slate-500" />
        <span>{`This form is on ${eform.ownerName?.trim() || 'a patient'}'s chart`}</span>
        <Link
          to={`/patients/${eform.ownerPatientId}`}
          className="ml-auto text-xs font-medium text-blue-700 underline-offset-2 hover:underline"
        >
          View patient
        </Link>
      </div>
    );
  }

  const completed = formatCalendarDate(eform.dateCompleted);
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
        <ClipboardList className="h-4 w-4 text-slate-500" />
        <span>{completed ? `Completed form: ${eform.title} · ${completed}` : `Completed form: ${eform.title}`}</span>
        {canAttach && (
          <button
            type="button"
            className="ml-auto inline-flex whitespace-nowrap items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
            onClick={() => setPickerOpen(true)}
          >
            Add form to a patient…
          </button>
        )}
      </div>

      {canAttach && (
        <Modal isOpen={pickerOpen} onClose={() => setPickerOpen(false)} title="Add form to a patient" size="md">
          <PatientSearchInput
            onSelect={(p) => {
              if (!p) return;
              setPickerOpen(false);
              onChoosePatient({
                patientId: p.patientId,
                firstName: p.firstName,
                lastName: p.lastName,
                dateOfBirth: p.dateOfBirth,
              });
            }}
            placeholder="Search by name, email, or phone…"
          />
        </Modal>
      )}
    </>
  );
}

export function LeadFormAttachConfirm({
  leadId,
  eform,
  patient,
  onClose,
}: {
  leadId: number;
  eform: LeadEform;
  /** The patient chosen; null keeps the dialog closed. */
  patient: FormAttachPatient | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // ConfirmDialog closes itself once onConfirm resolves. On a refusal we
  // swallow the error (so it is not an unhandled rejection) and set this flag,
  // so the close it then requests is ignored and the message stays on screen.
  const keepConfirmOpen = useRef(false);

  const name = patient ? patientName(patient) : '';
  const dob = patient ? formatCalendarDate(patient.dateOfBirth) : null;

  const handleConfirm = async () => {
    if (!patient) return;
    setError(null);
    try {
      await attachLeadForm(patient.patientId, leadId);
    } catch (err) {
      setError(getErrorMessage(err, ATTACH_FAILED_FALLBACK));
      keepConfirmOpen.current = true;
      return;
    }
    for (const key of [
      ['lead', leadId],
      // The server marks the lead converted, so it leaves the open-leads lists.
      ['leads'],
      ['lead-counts'],
      ['patient-forms-list', patient.patientId],
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
    toast.success(`Form added to ${name}'s chart.`);
  };

  const handleClose = () => {
    if (keepConfirmOpen.current) {
      keepConfirmOpen.current = false;
      return;
    }
    setError(null);
    onClose();
  };

  return (
    <ConfirmDialog
      isOpen={patient !== null}
      onClose={handleClose}
      onConfirm={handleConfirm}
      variant="warning"
      title="Add form to chart?"
      message={
        <>
          {`Add “${eform.title}” to ${name}'s chart? This also closes the lead.`}
          <span className="mt-2 block text-slate-600">
            {dob ? `Date of birth ${dob}` : 'Date of birth unknown'}
          </span>
          {error && (
            <span role="alert" className="mt-2 block text-red-600">
              {error}
            </span>
          )}
        </>
      }
      confirmLabel="Yes, add it"
      cancelLabel="Go back"
    />
  );
}
